import { useState, useRef, useMemo, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router';
import { Box, Stack, Typography, Paper, Divider, Chip } from '@mui/material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import ButtonLoading from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import ActivityIndicator from 'components/ActivityIndicatorComponent';
import { Formik, FormikProps } from 'formik';
import { TenantHomeDashboardNavigationBar } from 'components/LayoutComponent/VerticalLayout/HeaderBar';
import { PATH_CONSTANT } from 'constant/PathConstant';
import { useListCurrentSystemDropdown } from 'services/v1/TenantMaster/MasterTable/SystemTableDataService';
import { useGetListOrganizationDropdown } from 'services/v1/TenantMaster/MasterTable/TenantTableDataService';
import { TenantDetails } from 'types/api/TenantMaster/MasterTable/TenantTableDataTypes';
import { useUsersForDeployTenant } from 'services/v1/SystemTenant/TenantTableData';
import { useServerListDropdown } from 'services/v1/TenantMaster/MasterTable/ServerTableDataService';
import { CreateAndDeployTenantValidationSchema } from 'config/FormValidaton/TenantMaster/MasterTable/TenantTableValidationSchema';
import { useTenantDetails, useUpdateTenant } from 'services/v1/TenantMaster/MasterTable/TenantTableDataService';
import {
  getRolesBySelectedTenantCode,
  autocompleteStyles,
  userSelectionDataGridStyle,
  textInputAutoCompleteStyles,
} from './AddTenantPage';
import { useSearchParams } from 'react-router-dom';

const formInitialValues: TenantDetails = {
  serverId: null,
  systemId: null,
  displayName: null,
  organizationId: null,
  userId: [],
};

const sectionHeaderStyles = {
  color: '#3B4797',
  fontWeight: 600,
  fontSize: 14,
};

const sectionDescriptionStyles = {
  color: '#6B7280',
  fontSize: 12,
  mt: 0.5,
};

const fieldLabelStyles = {
  fontSize: 13,
  fontWeight: 500,
  color: '#374151',
  minWidth: 140,
  mb: 0.5,
};

const sectionPaperStyles = {
  p: 2.5,
  border: '1px solid #E3EBF6',
  borderRadius: 2,
  boxShadow: 'none',
};

function SectionHeader({ title, description }: { title: string; description: string }) {
  return (
    <Box mb={2}>
      <Typography sx={sectionHeaderStyles}>{title}</Typography>
      <Typography sx={sectionDescriptionStyles}>{description}</Typography>
    </Box>
  );
}

function FormField({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Stack spacing={0.5} sx={{ mt: 2 }}>
      <Typography sx={fieldLabelStyles}>
        {label}
        {required && <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>}
      </Typography>
      {children}
    </Stack>
  );
}

export default function EditTenantPage() {
  const formikRef = useRef<FormikProps<TenantDetails>>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id } = useParams();
  const [selectedFormValue, setSelectedFormValue] = useState<TenantDetails>(formInitialValues);
  const { data: allSystemListDropdown, isLoading: isLoadingSystem } = useListCurrentSystemDropdown();
  const { data: organizationsDropdown, isLoading: isLoadingOrganization } = useGetListOrganizationDropdown(
    selectedFormValue.systemId?.value || undefined,
  );
  const { data: usersData, isLoading: isLoadingUsers } = useUsersForDeployTenant(
    getRolesBySelectedTenantCode(selectedFormValue.organizationId?.label),
    selectedFormValue.organizationId?.label,
  );
  const { data: serverOptions, isLoading: isLoadingServer } = useServerListDropdown();
  const { data: tenantDetails, isLoading } = useTenantDetails(id);
  const { mutate: updateTenant, isLoading: isDeploying, error, isError } = useUpdateTenant();
  const isAnyLoading = isLoadingSystem || isLoadingOrganization || isLoadingServer || isLoadingUsers;

  const columns: GridColDef[] = [
    { field: 'fullName', headerName: 'Username', width: 180 },
    { field: 'roleType', headerName: 'Role Type', width: 80 },
    { field: 'roleName', headerName: 'Role Name', width: 170 },
    { field: 'userName', headerName: 'Email', width: 240 },
  ];

  const selectedIds = useMemo(() => {
    if (selectedFormValue.userId) {
      return selectedFormValue.userId.map((user) => user.id);
    }
    return [];
  }, [selectedFormValue.userId]);

  const handleOnSave = (data: TenantDetails) => {
    const payloadData = {
      systemId: data.systemId?.value || null,
      serverId: data.serverId?.value || null,
      organizationId: data.organizationId?.value || null,
      userId: data.userId,
      displayName: data.displayName,
      id,
    };
    updateTenant(payloadData, {
      onSuccess: () => {
        handleOnCancelInput();
        toast.success('Tenant has been updated successfully!');
      },
    });
  };

  useEffect(() => {
    if (tenantDetails) {
      formikRef.current?.setValues(tenantDetails.data);
      setSelectedFormValue(tenantDetails.data);
    }
  }, [tenantDetails]);

  const handleOnCancelInput = () => {
    const url =
      searchParams.get('refBackParam') != null
        ? searchParams.get('refBackParam')
        : PATH_CONSTANT.TENANT_MASTER.MASTER_TABLE.TENANT;
    const redirectPath = decodeURIComponent(url as string);
    navigate(redirectPath);
  };

  return (
    <TenantHomeDashboardNavigationBar>
      <Box sx={{ height: '100%', backgroundColor: '#F8F9FC' }}>
        {isLoading ? (
          <Stack direction='column' sx={{ height: 500 }} justifyContent='center'>
            <ActivityIndicator />
          </Stack>
        ) : (
          <Formik
            innerRef={formikRef}
            initialValues={formInitialValues}
            validationSchema={CreateAndDeployTenantValidationSchema}
            onSubmit={handleOnSave}
          >
            {(formikProps: FormikProps<TenantDetails>) => {
              const { values, setFieldValue, handleChange, handleBlur, errors, touched } = formikProps;
              return (
                <Box sx={{ maxWidth: 820, mx: 'auto', py: 3, px: 2 }}>
                  {/* Page Header */}
                  <Stack direction='row' alignItems='center' justifyContent='space-between' mb={3}>
                    <Box>
                      <Stack direction='row' alignItems='center' spacing={1.5}>
                        <Typography
                          variant='body1'
                          component='h2'
                          fontWeight='bold'
                          sx={{ color: '#3B4797', fontSize: 18 }}
                        >
                          Edit Tenant
                        </Typography>
                        {tenantDetails?.data?.displayName && (
                          <Chip
                            label={tenantDetails.data.displayName}
                            size='small'
                            sx={{ backgroundColor: '#EDF2FF', color: '#3B4797', fontWeight: 500, fontSize: 12 }}
                          />
                        )}
                      </Stack>
                      <Typography sx={{ color: '#6B7280', fontSize: 13, mt: 0.5 }}>
                        Update tenant settings and user assignments. System, organisation, and server cannot be changed
                        after creation.
                      </Typography>
                    </Box>
                    <Button
                      size='small'
                      onClick={handleOnCancelInput}
                      variant='main-table-panel-border'
                      disabled={isDeploying}
                    >
                      Cancel
                    </Button>
                  </Stack>

                  {/* Section 1: Tenant Configuration */}
                  <Paper sx={sectionPaperStyles}>
                    <SectionHeader
                      title='Tenant Configuration'
                      description='These settings were defined during tenant creation. Only the display name can be modified.'
                    />
                    <Divider sx={{ mb: 1 }} />

                    <FormField label='System' required>
                      <Autocomplete
                        options={allSystemListDropdown?.data || []}
                        // @ts-ignore
                        value={values.systemId}
                        disabled
                        getOptionLabel={(option) => option.label}
                        clearIcon={null}
                        contentEditable={false}
                        sx={autocompleteStyles}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            sx={{
                              ...textInputAutoCompleteStyles,
                              '& .MuiOutlinedInput-root.Mui-disabled': { backgroundColor: '#F9FAFB' },
                            }}
                            placeholder='Select System'
                            size='small'
                          />
                        )}
                      />
                    </FormField>

                    <FormField label='Organisation' required>
                      <Autocomplete
                        options={organizationsDropdown?.data || []}
                        // @ts-ignore
                        value={values.organizationId}
                        disabled
                        getOptionLabel={(option) => option.label}
                        clearIcon={null}
                        contentEditable={false}
                        sx={autocompleteStyles}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            sx={{
                              ...textInputAutoCompleteStyles,
                              '& .MuiOutlinedInput-root.Mui-disabled': { backgroundColor: '#F9FAFB' },
                            }}
                            placeholder={isAnyLoading ? 'Loading...' : 'Select Organisation'}
                            size='small'
                          />
                        )}
                      />
                    </FormField>

                    <FormField label='Server' required>
                      <Autocomplete
                        options={serverOptions || []}
                        // @ts-ignore
                        value={values.serverId}
                        disabled
                        getOptionLabel={(option) => option.label}
                        clearIcon={null}
                        contentEditable={false}
                        sx={autocompleteStyles}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            sx={{
                              ...textInputAutoCompleteStyles,
                              '& .MuiOutlinedInput-root.Mui-disabled': { backgroundColor: '#F9FAFB' },
                            }}
                            placeholder={isAnyLoading ? 'Loading...' : 'Select Server'}
                            size='small'
                          />
                        )}
                      />
                    </FormField>

                    <FormField label='Display Name'>
                      <TextField
                        fullWidth
                        size='small'
                        placeholder='e.g. Teamwork — a friendly name for this tenant'
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values?.displayName}
                        name='displayName'
                        error={touched.displayName && Boolean(errors.displayName)}
                        helperText={touched.displayName && errors.displayName}
                      />
                    </FormField>
                  </Paper>

                  {/* Section 2: User Assignment */}
                  <Paper sx={{ ...sectionPaperStyles, mt: 2.5 }}>
                    <SectionHeader
                      title='User Assignment'
                      description='Manage which users have access to this tenant. Users are filtered based on the organisation and their roles.'
                    />
                    <Divider sx={{ mb: 2 }} />

                    <div style={{ height: 280, width: '100%' }}>
                      {!isLoadingUsers && (
                        <DataGridPro
                          loading={isLoadingUsers}
                          selectionModel={selectedIds}
                          sx={userSelectionDataGridStyle}
                          density='compact'
                          rows={usersData?.data || []}
                          columns={columns}
                          onSelectionModelChange={(newSelection) => {
                            if (usersData?.data) {
                              const newUserIdValues = newSelection.map((id: any) => {
                                return usersData.data.find((user) => user.id === id);
                              });
                              // @ts-ignore
                              setSelectedFormValue((prevState) => ({
                                ...prevState,
                                userId: newUserIdValues,
                              }));
                              setFieldValue('userId', newUserIdValues);
                            }
                          }}
                          pagination={false}
                          hideFooter
                          rowsPerPageOptions={[5]}
                          checkboxSelection={
                            !isAnyLoading || (!!values.systemId && !!values.organizationId && !!values.serverId)
                          }
                        />
                      )}
                    </div>
                    {selectedIds.length > 0 && (
                      <Typography sx={{ fontSize: 12, color: '#3B4797', mt: 1, fontWeight: 500 }}>
                        {selectedIds.length} user{selectedIds.length > 1 ? 's' : ''} selected
                      </Typography>
                    )}
                  </Paper>

                  {/* Error Alert */}
                  {isError && (
                    <Alert severity='error' sx={{ mt: 2.5, borderRadius: 2 }}>
                      {error.response?.data.message}
                    </Alert>
                  )}

                  {/* Action Buttons */}
                  <Stack direction='row' spacing={2} justifyContent='flex-end' sx={{ mt: 3 }}>
                    <Button disabled={isDeploying} onClick={handleOnCancelInput} variant='main-table-panel-border'>
                      Cancel
                    </Button>
                    <ButtonLoading
                      loading={isDeploying}
                      variant='main-table-panel'
                      onClick={() => formikProps.handleSubmit()}
                      disabled={!CreateAndDeployTenantValidationSchema?.isValidSync(formikProps.values) || isDeploying}
                    >
                      Save Changes
                    </ButtonLoading>
                  </Stack>
                </Box>
              );
            }}
          </Formik>
        )}
      </Box>
    </TenantHomeDashboardNavigationBar>
  );
}
